import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatIndianDate, getStatusColorClass } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, UserCircle, Upload } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const EmployeeManager = () => {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [idCard, setIdCard] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    designation: '',
    role: '',
    username: '',
    password: '',
    status: 'Active',
    profile_photo: null,
    id_card: null
  });

  // Default system roles as fallback
  const defaultRoles = [
    { id: 'admin', name: 'Admin' },
    { id: 'back_office', name: 'Back Office Executive' },
    { id: 'service_engineer', name: 'Service Engineer' },
    { id: 'sales_marketing', name: 'Sales & Marketing' }
  ];

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles/');
      if (response.data && response.data.length > 0) {
        setRoles(response.data);
      } else {
        // Use default roles if none exist
        setRoles(defaultRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles(defaultRoles);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await api.get('/employees/');
      // Sort by latest first (created_at descending)
      const sortedEmployees = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setEmployees(sortedEmployees);
    } catch (error) {
      toast.error('Failed to fetch employees');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setSelectedEmployee(employee);
      setFormData({
        name: employee.name,
        mobile: employee.mobile,
        email: employee.email,
        designation: employee.designation,
        role: employee.role,
        username: employee.username,
        password: '',
        status: employee.status,
        profile_photo: employee.profile_photo,
        id_card: employee.id_card
      });
    } else {
      setSelectedEmployee(null);
      setFormData({
        name: '',
        mobile: '',
        email: '',
        designation: '',
        role: '',
        username: '',
        password: '',
        status: 'Active',
        profile_photo: null,
        id_card: null
      });
    }
    setProfilePhoto(null);
    setIdCard(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEmployee(null);
    setProfilePhoto(null);
    setIdCard(null);
    setFormData({
      name: '',
      mobile: '',
      email: '',
      designation: '',
      role: '',
      username: '',
      password: '',
      status: 'Active',
      profile_photo: null,
      id_card: null
    });
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Profile photo must be JPG or PNG');
      return;
    }
    
    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Profile photo must be less than 2MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, profile_photo: response.data.path }));
      toast.success('Profile photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
      console.error(error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleIdCardUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('ID card must be PDF or JPG');
      return;
    }
    
    // Validate file size (3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('ID card must be less than 3MB');
      return;
    }
    
    setUploadingId(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/id-card', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, id_card: response.data.path }));
      toast.success('ID card uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID card');
      console.error(error);
    } finally {
      setUploadingId(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedEmployee) {
        // Update employee
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await api.put(`/employees/${selectedEmployee.id}`, updateData);
        toast.success('Employee updated successfully');
      } else {
        // Create employee
        await api.post('/employees/', formData);
        toast.success('Employee created successfully');
      }
      handleCloseDialog();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error('Error:', error);
      console.error('Error response:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Manager</h1>
          <p className="text-gray-600 mt-1">Manage system users and their roles</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-employee-button">
          <Plus size={20} className="mr-2" />
          Add Employee
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employees</CardTitle>
          <CardDescription>View and manage all employees</CardDescription>
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !employees.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserCircle size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No employees found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium">{employee.name}</TableCell>
                      <TableCell>{employee.username}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell>{employee.mobile}</TableCell>
                      <TableCell>
                        <Badge variant={employee.role === 'Admin' ? 'default' : 'secondary'}>
                          {employee.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={employee.status === 'Active' ? 'default' : 'destructive'}
                          className={getStatusColorClass(employee.status)}
                        >
                          {employee.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(employee)}
                          >
                            <Edit size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>
              {selectedEmployee ? 'Update employee information' : 'Create a new employee account'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="designation">Designation *</Label>
                <Input
                  id="designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id || role.name} value={role.name}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={selectedEmployee !== null}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  Password {selectedEmployee ? '(leave blank to keep current)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!selectedEmployee}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="profile_photo">Profile Photo (JPG/PNG, Max 2MB)</Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handlePhotoUpload(file);
                    }
                  }}
                  disabled={uploadingPhoto}
                />
                {uploadingPhoto && <p className="text-sm text-blue-600">Uploading...</p>}
                {formData.profile_photo && (
                  <p className="text-sm text-green-600">✓ Photo uploaded</p>
                )}
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="id_card">ID Card (PDF/JPG, Max 3MB)</Label>
                <Input
                  id="id_card"
                  type="file"
                  accept="application/pdf,image/jpeg,image/jpg,image/png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleIdCardUpload(file);
                    }
                  }}
                  disabled={uploadingId}
                />
                {uploadingId && <p className="text-sm text-blue-600">Uploading...</p>}
                {formData.id_card && (
                  <p className="text-sm text-green-600">✓ ID card uploaded</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : selectedEmployee ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default EmployeeManager;
